define([
  "jquery",
  "underscore",
  "messaging/messenger",
  "configuration/properties",
  "mixpanel-browser",
], function ($, _, Messenger, Properties, mixpanelModule) {
  "use strict";

  const ID = "ID";
  const ID_TYPE = "ID Type";
  const ITEM_COUNT = "# Items";
  const KNOWLEDGE_BASE = "Knowledge Base";
  const SECTION_LEVEL_COL = "Section Level Col";
  const SECTION_URL = "Section URL";
  const VIEWED_PAGE_EVENT = "Viewed Page";
  const USER_INTERACTION_EVENT = "User Interaction";

  let lastPageUrl = "";
  // Adjust for the fact that Marionette might have come from node_modules, in which case it includes a "default"
  // value at the top level that is really what we want
  // noinspection JSUnresolvedVariable
  const mixpanel = mixpanelModule.default || mixpanelModule.mixpanel || mixpanelModule;
  let applicationName;
  let mixpanelInitialized = false;
  let productName;

  function _buildSection(level) {
    return `Section Level ${level}`;
  }

  function _normalizeName(value) {
    if (!value) {
      return "";
    }
    value = value.trim();
    if (!value) {
      return "";
    }
    // If the value already has spaces, then assume it has already been normalized
    if (value.indexOf(" ") > 0) {
      return value;
    }
    // Certain special cases that need to be overridden
    switch (value.toLowerCase()) {
      case "categories":
        return "Category";
      case "id":
        return ID;
      case "idtype":
        return ID_TYPE;
      case "itemcount":
        return ITEM_COUNT;
      case "prompt":
        return "Variable Prompt";
      case "quickcreate":
        return "Quick Create";
      case "sectionurl":
        return SECTION_URL;
      case "standard":
        return "Standard Response";
      case "url":
        return "URL";
    }
    // Capitalize the first letter of the word
    let result = value[0].toUpperCase();
    for (let i = 1; i < value.length; ++i) {
      const currentChar = value[i];
      // Split into separate words at capital letters
      if (currentChar.toUpperCase() == currentChar) {
        result += " ";
      }
      result += currentChar;
    }
    // URLs use plurals for the overall collection and singular for the individual entry.
    // But, end users would consider it to be the same "section", so force the value to be singular.
    // I know this is very simple, but any plurals that require more fancy processing (like categories) can be added
    // to the overrides at the top.
    if (result.length > 2 && result[result.length - 1] == "s") {
      result = result.slice(0, result.length - 1);
    }
    return result;
  }

  function _onHashchange() {
    if (!mixpanelInitialized) {
      return;
    }
    Messenger.vent.trigger("track:page");
  }

  function _forceBoolean(value) {
    if (_.isUndefined(value) || _.isNull(value)) {
      return false;
    }
    return value;
  }

  function _onMixpanelInit(userInformation) {
    // noinspection JSUnresolvedVariable
    applicationName = userInformation.applicationName || Properties.applicationName;
    productName = userInformation.productName || Properties.mixpanelProductName;
    const mixpanelProperties = {
      Application: applicationName,
      "Account ID": userInformation.tenantId || 0,
      "Account Name": userInformation.tenantName || "Global",
      "is SSO": _forceBoolean(userInformation.userSso),
      Product: productName,
      "User ACL ID": userInformation.userId,
      "User ID": userInformation.loginName,
      "User Internal": _forceBoolean(userInformation.userInternal),
      "Shadow Login": _forceBoolean(userInformation.shadowLogin),
    };
    // noinspection JSUnresolvedVariable
    const mixpanelToken = userInformation.mixpanelToken || Properties.mixpanelToken;
    if (mixpanelToken) {
      mixpanel.init(mixpanelToken);
      mixpanelInitialized = true;
      mixpanel.identify(userInformation.userId);
      mixpanel.register(mixpanelProperties);
      _onHashchange();
    } else {
      mixpanelInitialized = false;
    }
  }

  function _onMixpanelTrackInteraction(options) {
    if (mixpanelInitialized) {
      let trackOptions = _updateOptions(options);
      mixpanel.track(USER_INTERACTION_EVENT, trackOptions);
    }
  }

  function _onMixpanelTrackPage(options) {
    if (mixpanelInitialized) {
      let trackOptions = _updateOptions(options);
      const pageUrl = trackOptions[SECTION_URL];
      // Prevent logging the same page twice in a row
      if (lastPageUrl == pageUrl) {
        return;
      }
      lastPageUrl = pageUrl;
      mixpanel.track(VIEWED_PAGE_EVENT, trackOptions);
    }
  }

  function _splitIds(ids) {
    if (ids === null || ids === undefined) {
      return [];
    }
    if (Array.isArray(ids)) {
      return ids;
    }
    if (typeof ids != "string") {
      ids = ids.toString();
    }
    return ids.split(";");
  }

  function _updateOptions(options) {
    const result = {};
    let appendUrl = null;
    if (options) {
      for (let key of _.keys(options)) {
        if (key == "appendUrl") {
          appendUrl = options[key];
        } else {
          result[_normalizeName(key)] = options[key];
        }
      }
    }
    if (!result[SECTION_URL] && window.location.hash) {
      let newHash = window.location.hash;
      if (newHash[0] == "#") {
        newHash = newHash.slice(1);
      }
      if (appendUrl) {
        if (newHash.slice(-1) == "/") {
          newHash += appendUrl;
        } else {
          newHash += "/" + appendUrl;
        }
      }
      const hashParts = newHash.split("/");
      const ids = _splitIds(result[ID]);
      const idTypes = _splitIds(result[ID_TYPE]);
      const sections = [productName + " Home"];
      let stopNextPart = false;
      result[SECTION_URL] = newHash;
      for (let i = 0; i < hashParts.length; ++i) {
        if (/\d+/.test(hashParts[i])) {
          ids.push(hashParts[i]);
          if (i === 0) {
            // If the first ID is the KB, keep going
            idTypes.push(KNOWLEDGE_BASE);
          } else {
            idTypes.push(sections[sections.length - 1]);
          }
        } else {
          sections.push(_normalizeName(hashParts[i]));
        }
        if (hashParts[i] == "reports") {
          stopNextPart = true;
        } else if (stopNextPart) {
          break;
        }
      }
      for (let i = 0; i < sections.length; ++i) {
        const level = i + 1;
        result[_buildSection(level)] = sections[i];
        result[SECTION_LEVEL_COL] = sections[i];
      }
      if (ids.length > 0) {
        result[ID] = ids.join(";");
        result[ID_TYPE] = idTypes.join(";");
      }
    } else if (!result[SECTION_LEVEL_COL]) {
      for (let level = 5; level > 0; --level) {
        const sectionLevel = result[_buildSection(level)];
        if (sectionLevel) {
          result[SECTION_LEVEL_COL] = sectionLevel;
          break;
        }
      }
    }
    if (Array.isArray(result[ID])) {
      result[ID] = result[ID].join(";");
    }
    if (Array.isArray(result[ID_TYPE])) {
      result[ID_TYPE] = result[ID_TYPE].join(";");
    }
    return result;
  }

  Messenger.vent.on("mixpanel:init", _onMixpanelInit);
  Messenger.vent.on("track:page", _onMixpanelTrackPage);
  Messenger.vent.on("track:interaction", _onMixpanelTrackInteraction);
  $(window).on("hashchange", _onHashchange);
});
